<template>
  <MainLayout>
    <v-layout style="padding: 1em" justify-center row fill-height>
      <v-flex>
        <v-card style="height: 100%" light color="#f4f4fa">
          <v-layout column style="height: 100%">
            <!-- Title -->
            <v-card-title row class="align-center">
              <div class="headline flex-none">Complaints</div>
            </v-card-title>

            <!-- Filters -->
            <div class="d-flex flex-xs-column justify-end align-center">
              <!-- Search Field -->
              <v-text-field
                class="mx-4 flex"
                v-model="filters.search"
                label="Search by complaint id, citizen name, department name"
                clearable
                prepend-icon="search"
                @input="getComplaints"
              />

              <div
                class="flex-none d-flex flex-xs-column justify-end align-center"
              >
                <!-- Filter by Category -->
                <v-select
                  class="flex-none mx-4"
                  v-model="filters.categoryId"
                  :items="categories"
                  item-value="id"
                  item-text="name"
                  clearable
                  prepend-icon="category"
                  label="Select Category"
                  @change="getComplaints"
                />

                <!-- Filter by Status  -->
                <v-select
                  class="flex-none mx-4"
                  v-model="filters.status"
                  :items="statuses"
                  item-value="id"
                  item-text="name"
                  prepend-icon="playlist_add_check"
                  clearable
                  label="Select Status"
                  style="width: 11em"
                  @change="getComplaints"
                />

                <!-- Filter Date Range -->
                <div class="flex-none d-flex mx-4">
                  <DatePicker
                    v-model="filters.fromDate"
                    clearable
                    label="From Date"
                    @change="getComplaints"
                    :input-style="{ width: '10em' }"
                  />

                  <DatePicker
                    v-model="filters.toDate"
                    clearable
                    label="To Date"
                    @change="getComplaints"
                    :input-style="{ width: '10em' }"
                  />
                </div>
              </div>
            </div>

            <!-- Content -->
            <v-container fluid fill-height class="fill-height flex">
              <v-layout style="padding: 0" class="fill-height flex">
                <!-- Table -->
                <v-flex sm12 class="fill-height flex" style="overflow-y: auto;">
                  <v-data-table
                    :headers="headers"
                    :items="complaints"
                    :loading="loading"
                    :pagination.sync="pagination"
                    :rows-per-page-items="[10, 50, 100]"
                    :total-items="pagination.totalItems"
                    @update:pagination="getComplaints"
                    class="elevation-1 fill-height flex"
                  >
                    <!-- When items = [] -->
                    <template v-slot:no-data>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "No matching records found"
                        }}
                      </div>
                    </template>

                    <!-- When items = undefined -->
                    <template v-slot:no-results>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "Data will be displayed in here"
                        }}
                      </div>
                    </template>

                    <!-- Data -->
                    <template v-slot:items="props">
                      <!-- No. -->
                      <td>{{ props.index + 1 }}</td>

                      <!-- Created By -->
                      <td>
                        {{
                          props.item.created_by && props.item.created_by.name
                        }}
                      </td>

                      <!-- Category -->
                      <td>
                        {{ props.item.category && props.item.category.name }}
                      </td>

                      <!-- Department -->
                      <td>
                        {{
                          props.item.category &&
                            props.item.category.department &&
                            props.item.category.department.name
                        }}
                      </td>

                      <!-- Ward Number -->
                      <td>
                        {{
                          (props.item.ward && props.item.ward.name) ||
                            "Out of Ward"
                        }}
                      </td>

                      <!-- Status -->
                      <td v-bind:style="{ color: props.item.status.color }">
                        {{ props.item.status && props.item.status.name }}
                      </td>

                      <!-- Created At -->
                      <td>
                        {{
                          convertDate(props.item.created_at) +
                            " " +
                            convertTime(props.item.created_at)
                        }}
                      </td>

                      <!-- Complain Logs -->
                      <td>
                        <v-btn
                          title="View details"
                          icon
                          @click="showComplaintDetails(props.item)"
                        >
                          <v-icon small>visibility</v-icon>
                        </v-btn>
                      </td>

                      <!-- Assign Complaint -->
                      <td align="center" v-if="checkPermissionAssignComplaint">
                        <v-btn
                          v-if="props.item.status.name === 'New Issue'"
                          style="text-transform: none; font-size: 0.75em;"
                          color="primary"
                          depressed
                          small
                          @click="attemptAssign(props.item)"
                        >
                          <v-icon class="mr-2">assignment_ind</v-icon>
                          Assign
                        </v-btn>

                        <v-btn
                          v-else-if="
                            props.item.status.name === 'Assigned' ||
                              props.item.status.name === 'Pending'
                          "
                          style="text-transform: none; font-size: 0.75em;"
                          color="primary"
                          depressed
                          small
                          @click="attemptReassign(props.item)"
                        >
                          <v-icon class="mr-2">assignment_turned_in</v-icon>
                          Reassign
                        </v-btn>
                      </td>
                    </template>
                  </v-data-table>
                </v-flex>

                <!-- Form Detail -->
                <v-dialog
                  max-width="1100"
                  v-model="showAssign"
                  content-class="complaint-log-dialog"
                >
                  <Assign
                    v-if="checkPermissionAssignComplaint"
                    :item="complaint"
                    @on-close-click="showAssign = false"
                    @assign="submitAssign"
                  />
                </v-dialog>
              </v-layout>
            </v-container>
          </v-layout>
        </v-card>
      </v-flex>

      <!-- Complaint Log Dialog -->
      <v-dialog
        v-if="selectedComplaint"
        max-width="1100"
        v-model="showDialog"
        content-class="complaint-log-dialog"
      >
        <ComplaintLog
          :complaint="selectedComplaint"
          :loading="loading"
          @on-close-click="showDialog = false"
          @new-comment="addCommentToSelectedComplaint"
        />
      </v-dialog>

      <!-- Assign Dialog -->
      <v-dialog
        max-width="300"
        v-model="showAssignForm"
        content-class="complaint-log-dialog"
      >
        <v-card v-if="assignItem" light>
          <v-layout column style="height: 100%">
            <v-toolbar color="primary" dark>
              <v-toolbar-title>Assign to Staff</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="showAssignForm = false">
                <v-icon>close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-container fluid fill-height>
              <v-layout column>
                <h3>{{ "ComplaintID-" + assignItem.id }}</h3>
                <v-select
                  :items="staffs"
                  item-value="id"
                  item-text="name"
                  v-model="assignForm.assigned_to"
                  label="Choose a staff"
                />
                <v-text-field
                  v-model="assignForm.remarks"
                  hide-details
                  flat
                  label="Remarks"
                />
              </v-layout>
            </v-container>
            <v-divider />

            <v-card-actions>
              <v-spacer />
              <v-toolbar color="primary" dark>
                <v-toolbar-title>Assign to Staff</v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="showAssignForm = false">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-toolbar>

              <v-container fluid fill-height>
                <v-layout column>
                  <h3>{{ "ComplaintID-" + assignItem.id }}</h3>

                  <v-select
                    :items="staffs"
                    item-value="id"
                    item-text="name"
                    v-model="assignForm.assigned_to"
                    label="Choose a staff"
                  />

                  <v-text-field
                    v-model="assignForm.remarks"
                    hide-details
                    flat
                    label="Remarks"
                  />
                </v-layout>
              </v-container>

              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn color="primary" flat @click="submitAssign">
                  Submit
                </v-btn>
              </v-card-actions>
            </v-card-actions>
          </v-layout>
        </v-card>
      </v-dialog>
    </v-layout>
  </MainLayout>
</template>

<script>
import moment from "moment";
import api from "@/api";
import ComplaintLog from "@/components/complaints/ComplaintLog";
import Assign from "./Assign";
import { mapState } from "vuex";
import DatePicker from "@/components/DatePicker";
import { PLATFORMS } from "@/constants/platforms";
import { debounce } from "@/utils/Debounce";

export default {
  metaInfo: {
    title: "Complaints",
    meta: [{ name: "Complaints", content: "complaints management" }]
  },

  components: {
    ComplaintLog,
    Assign,
    DatePicker
  },

  data() {
    return {
      complaint: null,
      onSubmit: false,
      showDialog: false,
      showAssignForm: false,
      showAssign: false,
      assignItem: false,
      assignForm: {
        assigned_to: undefined,
        remarks: undefined
      },
      selectedComplaint: undefined,
      datePiker1: false,
      datePiker2: false,
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: null,
        totalItems: 0
      },
      filters: {
        search: undefined,
        statuses: undefined,
        categoryId: undefined,
        fromDate: undefined,
        toDate: undefined
      },
      loading: false,
      complaints: undefined,
      statuses: ["Resolved", "New Issue", "Assigned"],
      categories: [],
      staffs: []
    };
  },

  computed: {
    ...mapState("auth", ["currentUser"]),

    checkPermissionAssignComplaint() {
      return this.currentUser.platform === PLATFORMS.DEPARTMENT;
    },

    headers() {
      const headers = [
        {
          text: "Complaint ID",
          width: 80,
          sortable: false
        },
        {
          text: "Name",
          sortable: false
        },
        {
          text: "Category",
          sortable: false
        },
        {
          text: "Department",
          sortable: false
        },
        {
          text: "Ward Number",
          sortable: false
        },
        {
          text: "Status",
          width: 200,
          sortable: false
        },
        {
          text: "Created At",
          width: 200,
          sortable: false
        },
        {
          text: "Complaint Log",
          width: 70,
          sortable: false
        }
      ];
      if (this.checkPermissionAssignComplaint) {
        headers.push({
          text: "Assign To",
          width: 70,
          sortable: false,
          align: "center"
        });
      }
      return headers;
    }
  },

  watch: {
    showDialog(val) {
      if (!val) {
        this.selectedComplaint = undefined;
      }
    }
  },

  created() {
    this.getCategoryCatalog();
    this.getStaffUsers();

    this.getComplaints();
    this.showComplaintFromRoute();
  },

  methods: {
    async showComplaintFromRoute() {
      if (!this.$route.query.logs) return;

      try {
        const res = await api.getComplaint(Number(this.$route.query.logs));

        this.showComplaintDetails(res.data.data);
      } catch (error) {
        this.$notify({ type: "error", message: error.message });
      }
    },

    convertTime(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleTimeString();
    },

    convertDate(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleDateString();
    },

    showComplaintDetails(item) {
      this.selectedComplaint = item;
      this.showDialog = true;
    },

    attemptAssign(item) {
      this.showAssign = true;
      this.complaint = item;
    },

    attemptReassign(item) {
      this.showAssign = true;
      this.complaint = item;
    },

    submitAssign() {
      this.complaint = null;
      this.showAssign = false;
      this.getComplaints();
    },

    async getStaffUsers() {
      const res = await api.getStaffUsers({
        page_size: 1000,
        page: 1,
        accepted_by_department: true
      });

      this.staffs = res.data.data;
    },

    async getCategoryCatalog() {
      const res = await api.getCategoryCatalog();
      this.categories = res.data.data;
    },

    getComplaints: debounce(async function() {
      try {
        this.loading = true;
        this.complaints = undefined;

        const res = await api.getComplaints({
          page_size: this.pagination.rowsPerPage,
          page: this.pagination.page,
          q: this.filters.search || undefined,
          statuses: this.filters.status ? [this.filters.status] : undefined,
          category_id: this.filters.categoryId,
          start_time: this.filters.fromDate
            ? moment(this.filters.fromDate)
                .startOf("day")
                .format()
            : undefined,
          end_time: this.filters.toDate
            ? moment(this.filters.toDate)
                .endOf("day")
                .format()
            : undefined
        });

        this.loading = false;
        this.complaints = res.data.data;
        this.pagination.totalItems = res.data.total;
      } finally {
        this.loading = false;
      }
    }, 500),

    addCommentToSelectedComplaint(comment) {
      if (!this.complaints.includes(this.selectedComplaint)) {
        const complaint = this.complaints.find(
          c => c.id === this.selectedComplaint.id
        );
        if (!complaint) return;

        complaint.activities.push(comment);
      }
    }
  }
};
</script>

<style lang="css" scoped>
.form-item-view {
  display: flex;
  width: 100%;
  align-items: center;
}

.form-item-label {
  width: 10em;
  text-align: right;
  margin-right: 1.25em;
  margin-bottom: 1.25em;
}

.header-contact {
  font-size: 1.25em;
  margin-bottom: 0.625em;
}

@media (max-width: 600px) {
  .complaint-log {
    height: auto;
  }
}
</style>

<style>
.complaint-log-dialog {
  margin-top: 90px;
}
</style>
